import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { getCurrencyRate } from "../services/currencyService";
import {
  loadInterestTable,
  loadGlobalConstant,
  loadLDSSuppliers,
  loadAirlines,
} from "../services/fhpService";
import {
  getUserDetails,
  isAuthenticated,
  isAdminUser,
} from "../services/authService";
import { startSessionValidityPolling } from "../services/sessionInvalidation";
import { isTripMasterSurface } from "../surfaces/tripMaster/tripMasterEnv";
import {
  isPublicSiteRoute,
  extractPublicSiteRefFromSearch,
} from "../features/vacationPackagesPublic/publicSiteIdentity";
import { fetchAgentEntitlements } from "../features/agentEntitlements/api/fetchAgentEntitlements";
import {
  agentUserHasResolvableSession,
  resolveAgentSessionId,
  resolveAgentSessionIdSync,
} from "../services/agentApiSession";
import { entitlementsDebug } from "../features/agentEntitlements/entitlementsDebug";
import { resolveWalletCommissionPercentage, resolveLdsHrMarginFromFeatures } from "../features/agentEntitlements/selectors";
import {
  fetchAgentCommissionSettings,
  saveAgentCommissionSettings as saveAgentCommissionSettingsApi,
} from "../services/agentCommissionSettingsApi";
import { runAppContextBaseCommissionPricing } from "../pricing/pricingEngine";

const USD_RATE_INFO = {
  currency: "ILS",
  targetCurrency: "USD",
};

const EUR_RATE_INFO = {
  currency: "ILS",
  targetCurrency: "EUR",
};

const GBP_RATE_INFO = {
  currency: "ILS",
  targetCurrency: "GBP",
};

function isPublicFlightsVacationPackagesRoute() {
  if (typeof window === "undefined") return false;
  if (isTripMasterSurface()) return true;
  try {
    const url = new URL(window.location.href);
    return (
      url.pathname === "/vacation-packages" &&
      url.searchParams.get("category") === "flights"
    );
  } catch {
    return false;
  }
}

/** FX loaded successfully from getCurrencyRate (admin / getRate API) */
export const FX_SOURCE_ADMIN_CONFIG = "admin_config";
/** FX from AppContext default until load fails or API returns no rate */
export const FX_SOURCE_FALLBACK_DEFAULT = "fallback_default";

const gcKey = {
  CREDIT_CARD_FEE: "CREDIT_CARD_FEE",
  HR_COMMISSION: "HR_COMMISSION",
  HR_COMMISSION_HOTELS: "HR_COMMISSION_HOTELS",
  AGENT_COMMISSION: "AGENT_COMMISSION",
  BAGGAGE_FEE_FLAT: "BAGGAGE_FEE_FLAT",
};

/** performGetFetch returns parsed JSON on success; network/HTML errors throw; backend may return { status: "error", message } with 200 */
function logAppStartupFetchFailure(endpoint, res, expectedHint) {
  const hint = expectedHint ? ` Expected: ${expectedHint}.` : "";
  if (res === null || res === undefined) {
    console.error(
      `[AppContext/start] ${endpoint}: response is null — HTTP/network error, or HTML/non-JSON (see fetchService logs).${hint}`
    );
    return;
  }
  if (typeof res === "object" && res.status === "error") {
    const msg =
      res.message ??
      (typeof res.error === "string" ? res.error : res.error?.message) ??
      res.exception ??
      "(no message in body)";
    console.error(
      `[AppContext/start] ${endpoint}: backend status=error —`,
      msg
    );
    return;
  }
  console.error(
    `[AppContext/start] ${endpoint}: unexpected response shape.${hint}`,
    res
  );
}

const AppContext = createContext();

function shouldSkipPublicInternalPricingBootstrap() {
  if (typeof window === "undefined") return false;
  if (extractPublicSiteRefFromSearch(window.location.search)) return true;
  return isPublicSiteRoute(window.location.pathname);
}

export function isNumber(value) {
  return typeof value === "number" && !isNaN(value);
}

export const round2 = (num) => {
  const n = Number(num);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
};

export const AppProvider = ({ children }) => {
  const areCommissionSettingsEqual = useCallback((a, b) => {
    if (!a || !b) return false;
    return (
      Number(a.fixed_hotel_commission || 0) === Number(b.fixed_hotel_commission || 0) &&
      Number(a.fixed_flight_commission || 0) === Number(b.fixed_flight_commission || 0)
    );
  }, []);

  const [state, setState] = useState({});
  const [globalConstant, setGlobalConstant] = useState({});

  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= 768);

  /** מצב תצוגה ייעודי לאדמין: מסתיר את הסיידבר הראשי ומשאיר רק תפריט אדמין + תוכן */
  const [adminFocusMode, setAdminFocusMode] = useState(false);

  /** Full JSON from POST /api/agents/me/entitlements (null until loaded or on error) */
  const [agentEntitlements, setAgentEntitlements] = useState(null);
  const [agentEntitlementsStatus, setAgentEntitlementsStatus] = useState("idle");
  const [agentEntitlementsError, setAgentEntitlementsError] = useState(null);
  const [agentEntitlementsLoading, setAgentEntitlementsLoading] = useState(false);
  const [agentCommissionSettings, setAgentCommissionSettings] = useState({
    fixed_hotel_commission: 0,
    fixed_flight_commission: 0,
  });
  const [agentCommissionSettingsStatus, setAgentCommissionSettingsStatus] =
    useState("idle");
  const [agentCommissionSettingsError, setAgentCommissionSettingsError] =
    useState(null);
  const [agentCommissionSettingsLoading, setAgentCommissionSettingsLoading] =
    useState(false);
  const entitlementsCacheRef = useRef({
    sessionKey: "",
    at: 0,
    data: null,
  });
  const ENTITLEMENTS_TTL_MS = 5 * 60 * 1000;

  const getHrCommissionPercent = useCallback(
    () => parseFloat(globalConstant[gcKey.HR_COMMISSION]) || 0,
    [globalConstant]
  );

  const getHrCommissionPercentHotels = useCallback(
    () => parseFloat(globalConstant[gcKey.HR_COMMISSION_HOTELS]) || parseFloat(globalConstant[gcKey.HR_COMMISSION]),
    [globalConstant]
  );


  const getAgentCommissionPercent = useCallback(
    () => parseFloat(globalConstant[gcKey.AGENT_COMMISSION]) || 0,
    [globalConstant]
  );

  const getBaggageFeeFlat = useCallback(
    () => parseFloat(globalConstant[gcKey.BAGGAGE_FEE_FLAT]) || 0,
    [globalConstant]
  );

  const [interestTable, setInterestTable] = useState(null);

  const getCreditCardFeePercent = useCallback(
    () => parseFloat(globalConstant[gcKey.CREDIT_CARD_FEE]) || 0,
    [globalConstant]
  );


  const [isGlobalConstantLoaded, _setIsGlobalConstantLoaded] = useState(false);

  const updateGlobalConstant = useCallback((newData) => {
    setGlobalConstant((prev) => ({
      ...prev,
      ...newData,
    }));
  }, []);

  const [ldsSuppliers, setLdsSuppliers] = useState([]);
  const [airlines, setAirlines] = useState([]);

  // State for showing/hiding HR commission price (default: hidden)
  const [showHrCommissionPrice, setShowHrCommissionPrice] = useState(false);

  // State for B2C mode with fixed 15% HR commission
  // Check sessionStorage on mount to persist B2C mode across navigation
  const [isB2CMode, setIsB2CMode] = useState(() => {
    return sessionStorage.getItem('isB2CMode') === 'true';
  });
  
  // Update sessionStorage when B2C mode changes (but avoid infinite loops)
  // Use ref to track last value to prevent unnecessary updates
  const lastB2CModeRef = useRef(isB2CMode);
  
  useEffect(() => {
    // Only update if B2C mode actually changed
    if (lastB2CModeRef.current !== isB2CMode) {
      lastB2CModeRef.current = isB2CMode;
      const currentStorageValue = sessionStorage.getItem('isB2CMode') === 'true';
      // Only update if there's a change to avoid unnecessary writes
      if (isB2CMode !== currentStorageValue) {
        if (isB2CMode) {
          sessionStorage.setItem('isB2CMode', 'true');
        } else {
          sessionStorage.removeItem('isB2CMode');
        }
      }
    }
  }, [isB2CMode]);

  const setIsGlobalConstantLoaded = useCallback(
    (isLoaded) => {
      if (isLoaded === isGlobalConstantLoaded) return;
      _setIsGlobalConstantLoaded(isLoaded);
    },
    [isGlobalConstantLoaded]
  );

  const loadGlobalConstantFromAPI = useCallback(async () => {
    try {
      const res = await loadGlobalConstant();

      if (res === null || res === undefined || res.status === "error") {
        logAppStartupFetchFailure(
          "GET /loadGlobalConstant",
          res,
          "{ GlobalConstant: [...] }"
        );
        setIsGlobalConstantLoaded(true);
        return;
      }
      if (!res.GlobalConstant || !Array.isArray(res.GlobalConstant)) {
        logAppStartupFetchFailure(
          "GET /loadGlobalConstant",
          res,
          "{ GlobalConstant: [...] }"
        );
        setIsGlobalConstantLoaded(true);
        return;
      }

      const extractVal = (v) => {
        const found = res.GlobalConstant.filter((x) => x[0] === v)[0];
        return found ? found[1] : null;
      };

      const gc = {
        [gcKey.CREDIT_CARD_FEE]: extractVal(gcKey.CREDIT_CARD_FEE),
        [gcKey.HR_COMMISSION]: extractVal(gcKey.HR_COMMISSION),
        [gcKey.HR_COMMISSION_HOTELS]: extractVal(gcKey.HR_COMMISSION_HOTELS),
        [gcKey.AGENT_COMMISSION]: extractVal(gcKey.AGENT_COMMISSION),
        [gcKey.BAGGAGE_FEE_FLAT]: extractVal(gcKey.BAGGAGE_FEE_FLAT),
      };
      setGlobalConstant(gc);
      setIsGlobalConstantLoaded(true);
    } catch (error) {
      console.error("[AppContext/start] GET /loadGlobalConstant: exception", error);
      setIsGlobalConstantLoaded(true);
    }
  }, [setIsGlobalConstantLoaded]);

  const loadLDSSuppliersFromAPI = useCallback(async () => {
    try {
      const res = await loadLDSSuppliers();

      if (res === null || res === undefined || res.status === "error") {
        logAppStartupFetchFailure(
          "GET /loadLDSSuppliers",
          res,
          "{ suppliers: [...] }"
        );
        return;
      }
      if (!Array.isArray(res.suppliers)) {
        logAppStartupFetchFailure(
          "GET /loadLDSSuppliers",
          res,
          "{ suppliers: [...] }"
        );
        return;
      }

      // Ensure all suppliers have commission_type (fallback to PERCENT if missing)
      const withoutCommissionType = res.suppliers.filter(s => s.commission_type === null || s.commission_type === undefined);
      if (withoutCommissionType.length > 0) {
        console.warn("⚠️ WARNING: Some suppliers are missing commission_type:", withoutCommissionType.map(s => s.supplier_name));
      }

      // הוסף ברירת מחדל ל-commission_type אם הוא חסר (fallback בפרונטאנד)
      const suppliersWithDefaults = res.suppliers.map(supplier => ({
        ...supplier,
        commission_type: supplier.commission_type || 'PERCENT', // ברירת מחדל ל-PERCENT
      }));
      
      setLdsSuppliers(suppliersWithDefaults);
    } catch (error) {
      console.error("[AppContext/start] GET /loadLDSSuppliers: exception", error);
    }
  }, []);

  const loadAirlinesFromAPI = useCallback(async () => {
    try {
      const res = await loadAirlines();

      if (res === null || res === undefined || res.status === "error") {
        logAppStartupFetchFailure(
          "GET /loadAirlines",
          res,
          "{ airlines: [...] }"
        );
        return;
      }
      if (!Array.isArray(res.airlines)) {
        logAppStartupFetchFailure(
          "GET /loadAirlines",
          res,
          "{ airlines: [...] }"
        );
        return;
      }

      setAirlines(res.airlines);
    } catch (error) {
      console.error("[AppContext/start] GET /loadAirlines: exception", error);
    }
  }, []);

  const loadInterestTableFromAPI = useCallback(async () => {
    try {
      const interestTableData = await loadInterestTable();

      if (
        interestTableData === null ||
        interestTableData === undefined ||
        interestTableData.status === "error"
      ) {
        logAppStartupFetchFailure(
          "GET /loadInterestTable",
          interestTableData,
          "non-null JSON body (table payload)"
        );
        return;
      }

      setInterestTable(interestTableData);
    } catch (error) {
      console.error("[AppContext/start] GET /loadInterestTable: exception", error);
    }
  }, []);

  const [USDrate, setUSDRate] = useState(3.7); // Default fallback rate
  const [EURrate, setEURRate] = useState(4.0); // Default fallback rate
  const [GBPrate, setGBPRate] = useState(4.5); // Default fallback rate

  const [currencyRateSource, setCurrencyRateSource] = useState({
    USD: FX_SOURCE_FALLBACK_DEFAULT,
    EUR: FX_SOURCE_FALLBACK_DEFAULT,
    GBP: FX_SOURCE_FALLBACK_DEFAULT,
  });

  const loadUSDRate = useCallback(async () => {
    try {
      const res = await getCurrencyRate(USD_RATE_INFO);
      const nextRate = Number(res?.rate) || 3.7;
      const nextSource = res?.rate
        ? FX_SOURCE_ADMIN_CONFIG
        : FX_SOURCE_FALLBACK_DEFAULT;
      if (res?.rate) {
        setUSDRate((prev) => (Object.is(prev, nextRate) ? prev : nextRate));
        try {
          if (typeof localStorage !== "undefined") {
            localStorage.setItem("currentRate", String(nextRate));
          }
        } catch {
          /* ignore */
        }
      } else {
        setUSDRate((prev) => (Object.is(prev, nextRate) ? prev : nextRate));
      }
      setCurrencyRateSource((prev) =>
        prev.USD === nextSource ? prev : { ...prev, USD: nextSource }
      );
    } catch (error) {
      console.error("Failed to fetch USD rate:", error);
      setUSDRate((prev) => (Object.is(prev, 3.7) ? prev : 3.7));
      setCurrencyRateSource((prev) =>
        prev.USD === FX_SOURCE_FALLBACK_DEFAULT
          ? prev
          : { ...prev, USD: FX_SOURCE_FALLBACK_DEFAULT }
      );
    }
  }, []);

  const loadEURRate = useCallback(async () => {
    try {
      const res = await getCurrencyRate(EUR_RATE_INFO);
      const nextRate = Number(res?.rate) || 4.0;
      const nextSource = res?.rate
        ? FX_SOURCE_ADMIN_CONFIG
        : FX_SOURCE_FALLBACK_DEFAULT;
      if (res?.rate) {
        setEURRate((prev) => (Object.is(prev, nextRate) ? prev : nextRate));
      } else {
        setEURRate((prev) => (Object.is(prev, nextRate) ? prev : nextRate));
      }
      setCurrencyRateSource((prev) =>
        prev.EUR === nextSource ? prev : { ...prev, EUR: nextSource }
      );
    } catch (error) {
      console.error("Failed to fetch EUR rate:", error);
      setEURRate((prev) => (Object.is(prev, 4.0) ? prev : 4.0));
      setCurrencyRateSource((prev) =>
        prev.EUR === FX_SOURCE_FALLBACK_DEFAULT
          ? prev
          : { ...prev, EUR: FX_SOURCE_FALLBACK_DEFAULT }
      );
    }
  }, []);

  const loadGBPRate = useCallback(async () => {
    try {
      const res = await getCurrencyRate(GBP_RATE_INFO);
      const nextRate = Number(res?.rate) || 4.5;
      const nextSource = res?.rate
        ? FX_SOURCE_ADMIN_CONFIG
        : FX_SOURCE_FALLBACK_DEFAULT;
      if (res?.rate) {
        setGBPRate((prev) => (Object.is(prev, nextRate) ? prev : nextRate));
      } else {
        setGBPRate((prev) => (Object.is(prev, nextRate) ? prev : nextRate));
      }
      setCurrencyRateSource((prev) =>
        prev.GBP === nextSource ? prev : { ...prev, GBP: nextSource }
      );
    } catch (error) {
      console.error("Failed to fetch GBP rate:", error);
      setGBPRate((prev) => (Object.is(prev, 4.5) ? prev : 4.5));
      setCurrencyRateSource((prev) =>
        prev.GBP === FX_SOURCE_FALLBACK_DEFAULT
          ? prev
          : { ...prev, GBP: FX_SOURCE_FALLBACK_DEFAULT }
      );
    }
  }, []);

  const calculateCreditCardFee = useCallback(
    (basePrice) => {
      const fee = parseFloat(basePrice) * (getCreditCardFeePercent() / 100);
      return round2(fee);
    },
    [getCreditCardFeePercent]
  );

  useEffect(() => {
    const handleResize = () => {
      const mobile = typeof window !== "undefined" && window.innerWidth <= 768;
      if (mobile === isMobile) return;
      setIsMobile(mobile);
    };

    handleResize(); // Set initial state
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    loadUSDRate();
    loadEURRate();
    loadGBPRate();
    // loadCreditCardFeeFromAPI();
    loadInterestTableFromAPI();
    loadAirlinesFromAPI();
  }, []);

  useEffect(() => {
    if (shouldSkipPublicInternalPricingBootstrap()) return;
    const sid = state?.user?.sessionID || state?.user?.sessionId;
    if (!sid) return;
    loadLDSSuppliersFromAPI();
    loadGlobalConstantFromAPI();
  }, [state.user, loadLDSSuppliersFromAPI, loadGlobalConstantFromAPI]);

  const calcBaseCommission = useCallback(
    (
      price,
      hrCommissionPercent = null,
      agentCommissionPercent = null,
      isHotel = false,
      options = null
    ) => {
      let hrMarginPercentOverride = null;
      let hrMarginSourceFeatureKey = null;
      let walletCommissionPercent = null;
      if (agentEntitlementsStatus === "ok" && agentEntitlements) {
        walletCommissionPercent = resolveWalletCommissionPercentage(agentEntitlements);
        if (isHotel && agentEntitlements.features) {
          const hrRes = resolveLdsHrMarginFromFeatures(
            agentEntitlements.features,
            "hotel"
          );
          if (hrRes.percent != null) {
            hrMarginPercentOverride = hrRes.percent;
            hrMarginSourceFeatureKey = hrRes.sourceFeatureKey;
          }
        }
      }

      const res = runAppContextBaseCommissionPricing({
        price,
        hrCommissionPercent,
        agentCommissionPercent,
        isHotel,
        hotelFinalRoundOnly: Boolean(options?.hotelFinalRoundOnly && isHotel),
        isB2CMode,
        getHrCommissionPercent,
        getHrCommissionPercentHotels,
        getAgentCommissionPercent,
        getCreditCardFeePercent,
        hrMarginPercentOverride,
        hrMarginSourceFeatureKey,
        walletCommissionPercent,
      });

      if (res.status === "missing_price") {
        return {
          initialPrice: null,
          priceWithCommission: null,
          priceWithCommissionAndCreditCardFee: null,
          creditCardFee: null,
          hrCommissionPercent: null,
          agentCommissionPercent: null,
          hrCommission: null,
          agentCommission: null,
          unifiedHrMarginPercent: null,
          unifiedHrMargin: null,
          handlingFee: 0,
          pricePreFinalRound: null,
          pricingStatus: "missing_price",
          auditSteps: res.auditSteps,
        };
      }

      return {
        ...res.ourPrice,
        pricingStatus: res.status,
        auditSteps: res.auditSteps,
      };
    },
    [
      getHrCommissionPercent,
      getHrCommissionPercentHotels,
      getAgentCommissionPercent,
      getCreditCardFeePercent,
      isB2CMode,
      agentEntitlements,
      agentEntitlementsStatus,
    ]
  );

  ///// login
  const appLogin = useCallback((user) => {
    if (!user) return;
    setState((prevState) => {
      const prevUser = prevState?.user;
      const prevSession = String(prevUser?.sessionID || prevUser?.sessionId || "");
      const nextSession = String(user?.sessionID || user?.sessionId || "");
      const prevId = String(prevUser?.id || "");
      const nextId = String(user?.id || "");
      const isSameIdentity =
        prevSession &&
        nextSession &&
        prevSession === nextSession &&
        prevId === nextId;
      if (isSameIdentity) return prevState;
      if (!prevUser && !nextSession && !nextId) return prevState;
      return {
        ...prevState,
        user,
      };
    });
  }, []);

  const appLogout = useCallback(() => {
    entitlementsCacheRef.current = { sessionKey: "", at: 0, data: null };
    setAgentEntitlements(null);
    setAgentEntitlementsStatus("idle");
    setAgentEntitlementsError(null);
    setAgentEntitlementsLoading(false);
    setState((prevState) => {
      if (!prevState?.user) return prevState;
      return {
        ...prevState,
        user: null,
      };
    });
  }, []);

  const loadAgentEntitlements = useCallback(async (options = {}) => {
    const force = options.force === true;
    const u = getUserDetails();
    const ut = String(u?.usertype || u?.userType || "").toLowerCase();
    if (ut !== "agents" && ut !== "agent") {
      setAgentEntitlements(null);
      setAgentEntitlementsStatus("idle");
      setAgentEntitlementsError(null);
      return;
    }
    let sessionKey = resolveAgentSessionIdSync();
    if (!sessionKey) {
      try {
        sessionKey = (await resolveAgentSessionId()) || null;
      } catch {
        sessionKey = null;
      }
    }
    if (!sessionKey) {
      setAgentEntitlements(null);
      setAgentEntitlementsStatus("error");
      setAgentEntitlementsError("no_session");
      entitlementsDebug("load skipped: no session (agentApiSession)");
      return;
    }
    const now = Date.now();
    const c = entitlementsCacheRef.current;
    if (
      !force &&
      c.sessionKey === sessionKey &&
      c.data &&
      now - c.at < ENTITLEMENTS_TTL_MS
    ) {
      setAgentEntitlements(c.data);
      setAgentEntitlementsStatus("ok");
      setAgentEntitlementsError(null);
      entitlementsDebug("cache hit", { sessionKey: sessionKey.slice(0, 6) });
      return;
    }

    setAgentEntitlementsLoading(true);
    setAgentEntitlementsStatus("loading");
    setAgentEntitlementsError(null);
    try {
      const data = await fetchAgentEntitlements();
      entitlementsCacheRef.current = {
        sessionKey,
        at: Date.now(),
        data,
      };
      setAgentEntitlements(data);
      setAgentEntitlementsStatus("ok");
      entitlementsDebug("loaded", {
        agentId: data?.agentId,
        nFeatures: Object.keys(data?.features || {}).length,
      });
    } catch (e) {
      entitlementsDebug("load failed", e?.message || e);
      setAgentEntitlements(null);
      setAgentEntitlementsStatus("error");
      setAgentEntitlementsError(e?.message || String(e));
    } finally {
      setAgentEntitlementsLoading(false);
    }
  }, []);

  const refreshAgentEntitlements = useCallback(() => {
    return loadAgentEntitlements({ force: true });
  }, [loadAgentEntitlements]);

  const loadAgentCommissionSettings = useCallback(async () => {
    setAgentCommissionSettingsLoading(true);
    setAgentCommissionSettingsStatus("loading");
    setAgentCommissionSettingsError(null);
    try {
      const data = await fetchAgentCommissionSettings();
      const normalized = {
        fixed_hotel_commission: Number(data?.fixed_hotel_commission) || 0,
        fixed_flight_commission: Number(data?.fixed_flight_commission) || 0,
      };
      setAgentCommissionSettings((prev) =>
        areCommissionSettingsEqual(prev, normalized) ? prev : normalized
      );
      setAgentCommissionSettingsStatus("ok");
    } catch (e) {
      const fallback = {
        fixed_hotel_commission: 0,
        fixed_flight_commission: 0,
      };
      setAgentCommissionSettings((prev) =>
        areCommissionSettingsEqual(prev, fallback) ? prev : fallback
      );
      setAgentCommissionSettingsStatus("error");
      setAgentCommissionSettingsError(e?.message || String(e));
    } finally {
      setAgentCommissionSettingsLoading(false);
    }
  }, []);

  const refreshAgentCommissionSettings = useCallback(() => {
    return loadAgentCommissionSettings();
  }, [loadAgentCommissionSettings]);

  const saveAgentCommissionSettings = useCallback(async (payload) => {
    setAgentCommissionSettingsLoading(true);
    setAgentCommissionSettingsError(null);
    try {
      const data = await saveAgentCommissionSettingsApi(payload);
      const normalized = {
        fixed_hotel_commission: Number(data?.fixed_hotel_commission) || 0,
        fixed_flight_commission: Number(data?.fixed_flight_commission) || 0,
      };
      setAgentCommissionSettings((prev) =>
        areCommissionSettingsEqual(prev, normalized) ? prev : normalized
      );
      setAgentCommissionSettingsStatus("ok");
      return normalized;
    } catch (e) {
      setAgentCommissionSettingsStatus("error");
      setAgentCommissionSettingsError(e?.message || String(e));
      throw e;
    } finally {
      setAgentCommissionSettingsLoading(false);
    }
  }, [areCommissionSettingsEqual]);

  // Use ref to track if we've already attempted to load user
  const userLoadAttempted = useRef(false);

  // isAppLoggedIn as a computed value instead of a function to avoid infinite loops
  const isAppLoggedInValue = useMemo(() => {
    return state.user != null;
  }, [state.user]);

  // isAppLoggedIn as a function for backward compatibility
  const isAppLoggedIn = useCallback(() => {
    return isAppLoggedInValue;
  }, [isAppLoggedInValue]);

  // Load user from localStorage on mount only (once)
  useEffect(() => {
    if (!userLoadAttempted.current) {
      userLoadAttempted.current = true;
      
      // Helper function to load user and handle agent session checks
      const loadUserFromStorage = (user) => {
        if (!user) return false;
        appLogin(user);
        return true;
      };
      
      // First, try to load user from localStorage immediately
      // This ensures the user is loaded even if the API check fails
      const user = getUserDetails();
      const userHasSession =
        user && (user.sessionID || user.sessionId);
      if (userHasSession) {
        loadUserFromStorage(user);
      } else {
        // Check if there's raw data in localStorage that might not be decrypting properly
        const rawUserInfo = localStorage.getItem('UserInfo');
        if (rawUserInfo) {
          try {
            const fallbackUser = JSON.parse(rawUserInfo);
            if (
              fallbackUser &&
              (fallbackUser.sessionID || fallbackUser.sessionId)
            ) {
              loadUserFromStorage(fallbackUser);
            }
          } catch (e) {
            // Ignore parsing errors
          }
        }
      }
      
      // Then check authentication status with the server (but don't log out on network errors)
      // This is done asynchronously and won't block the initial user load
      // Only check if we have a user loaded in state - if we have a user, verify with server
      // If we don't have a user yet, skip the check (user might not be loaded yet)
      if (isPublicFlightsVacationPackagesRoute()) {
        return;
      }
      setTimeout(() => {
        // Use setTimeout to ensure state.user is updated after loadUserFromStorage
        const currentUser = getUserDetails();
        if (
          currentUser &&
          (currentUser.sessionID || currentUser.sessionId)
        ) {
          isAuthenticated().then((result) => {
            if (result === false) {
              appLogout();
              userLoadAttempted.current = false;
            } else if (result === true) {
              const user = getUserDetails();
              if (user && !state.user) {
                loadUserFromStorage(user);
              }
            }
          }).catch(() => {
            const user = getUserDetails();
            if (user && !state.user) {
              loadUserFromStorage(user);
            }
          });
        }
      }, 100); // Small delay to ensure state is updated
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Lightweight session detection only — does not extend server expires_at.
  useEffect(() => {
    if (isPublicFlightsVacationPackagesRoute()) return;
    const user = state.user;
    if (!user) return;
    const sid = user.sessionID || user.sessionId;
    if (!sid) return;
    return startSessionValidityPolling();
  }, [state.user]);

  // Agent commercial entitlements (read-only API) — after user is in state with resolvable session
  useEffect(() => {
    if (isPublicFlightsVacationPackagesRoute()) return;
    const u = state.user;
    if (!u) return;
    const ut = String(u.usertype || u.userType || "").toLowerCase();
    if (ut !== "agents" && ut !== "agent") return;
    if (!agentUserHasResolvableSession(u)) {
      entitlementsDebug("load deferred: state.user without sessionID");
      return;
    }
    loadAgentEntitlements();
    loadAgentCommissionSettings();
  }, [state.user, loadAgentEntitlements, loadAgentCommissionSettings]);

  // Keyboard shortcut handler for admin: Shift + S to toggle HR commission price visibility
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check if Shift + S is pressed and user is admin
      if (e.shiftKey && (e.key === 'S' || e.key === 's')) {
        // Check if user is admin
        if (isAdminUser()) {
          e.preventDefault();
          setShowHrCommissionPrice((prev) => !prev);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      currencyRate: {
        USD: USDrate,
        EUR: EURrate,
        GBP: GBPrate,
      },
      currencyRateSource,
      isMobile,
      USDrate,
      loadUSDRate,
      EURrate,
      loadEURRate,
      GBPrate,
      loadGBPRate,
      getCreditCardFeePercent,
      getAgentCommissionPercent,
      getHrCommissionPercent,
      getHrCommissionPercentHotels,
      updateGlobalConstant,
      interestTable,
      setInterestTable,
      gcKey,
      globalConstant,
      calcBaseCommission,
      isGlobalConstantLoaded,
      calculateCreditCardFee,
      getBaggageFeeFlat,
      appLogin,
      appLogout,
      isAppLoggedIn,
      isAppLoggedInValue,
      getUserDetails,
      ldsSuppliers,
      setLdsSuppliers,
      airlines,
      setAirlines,
      loadLDSSuppliersFromAPI,
      showHrCommissionPrice,
      setShowHrCommissionPrice,
      isB2CMode,
      setIsB2CMode,
      agentEntitlements,
      agentEntitlementsStatus,
      agentEntitlementsError,
      agentEntitlementsLoading,
      loadAgentEntitlements,
      refreshAgentEntitlements,
      agentCommissionSettings,
      agentCommissionSettingsStatus,
      agentCommissionSettingsError,
      agentCommissionSettingsLoading,
      loadAgentCommissionSettings,
      refreshAgentCommissionSettings,
      saveAgentCommissionSettings,
      adminFocusMode,
      setAdminFocusMode,
    }),
    [
      USDrate,
      EURrate,
      GBPrate,
      currencyRateSource,
      isMobile,
      loadUSDRate,
      loadEURRate,
      loadGBPRate,
      getCreditCardFeePercent,
      getAgentCommissionPercent,
      getHrCommissionPercent,
      getHrCommissionPercentHotels,
      updateGlobalConstant,
      interestTable,
      globalConstant,
      calcBaseCommission,
      isGlobalConstantLoaded,
      calculateCreditCardFee,
      getBaggageFeeFlat,
      appLogin,
      appLogout,
      isAppLoggedIn,
      isAppLoggedInValue,
      getUserDetails,
      ldsSuppliers,
      airlines,
      loadLDSSuppliersFromAPI,
      showHrCommissionPrice,
      isB2CMode,
      agentEntitlements,
      agentEntitlementsStatus,
      agentEntitlementsError,
      agentEntitlementsLoading,
      loadAgentEntitlements,
      refreshAgentEntitlements,
      agentCommissionSettings,
      agentCommissionSettingsStatus,
      agentCommissionSettingsError,
      agentCommissionSettingsLoading,
      loadAgentCommissionSettings,
      refreshAgentCommissionSettings,
      saveAgentCommissionSettings,
      adminFocusMode,
    ]
  );

  const lastAppProviderSnapshotRef = useRef(null);
  if (import.meta.env.DEV) {
    const snapshot = {
      hasUser: Boolean(state?.user),
      isMobile,
      adminFocusMode,
      showHrCommissionPrice,
      isB2CMode,
      entitlementsStatus: agentEntitlementsStatus,
      entitlementsLoading: agentEntitlementsLoading,
      commissionSettingsStatus: agentCommissionSettingsStatus,
      commissionSettingsLoading: agentCommissionSettingsLoading,
      currencyRateSourceUSD: currencyRateSource?.USD,
      currencyRateSourceEUR: currencyRateSource?.EUR,
      currencyRateSourceGBP: currencyRateSource?.GBP,
      fixedHotelCommission: Number(agentCommissionSettings?.fixed_hotel_commission || 0),
      fixedFlightCommission: Number(agentCommissionSettings?.fixed_flight_commission || 0),
      usd: USDrate,
      eur: EURrate,
      gbp: GBPrate,
    };
    const prev = lastAppProviderSnapshotRef.current;
    const changedKeys = prev
      ? Object.keys(snapshot).filter((key) => !Object.is(snapshot[key], prev[key]))
      : Object.keys(snapshot);
    lastAppProviderSnapshotRef.current = snapshot;
    }

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useAppContext must be used within a AppProvider");
  }
  return context;
};

export default AppContext;
